/* eslint-disable import/no-cycle */
/* eslint import/no-cycle: [2, { maxDepth: 1 }] */
import { getAnalyticsQuestionName } from './analyticsNames';
import * as configs from './configs';
import { send } from '../analytics/send';
import { useAppStore, useQuestionsStore } from '../store';
import { markStepPassed } from '../utils/scripts/getUserData';

type StepConfigItem = {
  name: string;
  separationStep?: boolean;
  progressTitle?: string;
  hideHeader?: boolean;
  hideProgress?: boolean;
  [key: string]: unknown;
};

type StepDetails = {
  showActionBar?: boolean;
  continue?: boolean;
  continueAuto?: boolean;
  welcome_loader?: boolean;
  [key: string]: unknown;
};

type StepsDetailsMap = Record<string, StepDetails | undefined>;

type StepItem = {
  name: string;
  showActionBar?: boolean;
  isCompleted?: boolean;
};

type SetupArgs = {
  stepsConfig: StepConfigItem[];
  currentStep?: string | null;
  stepsDetails: StepsDetailsMap;
};

type ForwardArgs = {
  preventAction?: number;
  numberOfSteps?: number;
  preventContinueEvent?: boolean;
};

type BackArgs = {
  numberOfSteps?: number;
};

type UserMovedArgs = {
  byNextStep?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnswerMap = Record<string, any>;

let transitionTimeout: ReturnType<typeof setTimeout> | null = null;
let transitionBetweenStepsTime: number;
let preventActionFromFiring = false;
let preventActionTimer: ReturnType<typeof setTimeout> | undefined;
let dataSended = false;
let quizStarted = false;

function getTransitionBetweenStepsTime(): number {
  return parseInt(
    getComputedStyle(document.documentElement)
      .getPropertyValue('--transitionBetweenStepsTime')
      .slice(0, -2),
    10,
  );
}

function createStep(name: string, showActionBar?: boolean): StepItem {
  return { name, showActionBar };
}

function setCurrentStepIndex(): void {
  const stepsList = useAppStore.getState().list as StepItem[];
  const { currentStep } = useAppStore.getState();
  let currentStepIndex: number | null = null;

  stepsList.forEach((step, index) => {
    if (currentStep === step.name) {
      currentStepIndex = index;
    }
  });

  useAppStore.getState().setCurrentIndex(currentStepIndex);
}

function setPreviousAndNextStepsStatus(): void {
  const stepsList = useAppStore.getState().list as StepItem[];
  const currentStepIndex = useAppStore.getState().currentStepIndex as number;

  if (stepsList[currentStepIndex - 1]) {
    useAppStore.getState().setPreviousStep(stepsList[currentStepIndex - 1].name);
  } else {
    useAppStore.getState().setPreviousStep(null);
  }

  if (stepsList[currentStepIndex + 1]) {
    useAppStore.getState().setNextStep(stepsList[currentStepIndex + 1].name);
  } else {
    useAppStore.getState().setNextStep(null);
  }
}

function checkActionBarShowing(): void {
  const stepsList = useAppStore.getState().list as StepItem[];
  const currentStepIndex = useAppStore.getState().currentStepIndex as number;
  const actionBarShowed = stepsList[currentStepIndex].showActionBar;

  if (actionBarShowed) {
    document.body.classList.add('showActionBar');
  } else {
    document.body.classList.remove('showActionBar');
  }

  useAppStore.getState().setShowActionBar(!!actionBarShowed);
}

function navigateTo(stepName: string): void {
  if (transitionTimeout) clearTimeout(transitionTimeout);
  document.body.classList.add('onTransition');

  transitionTimeout = setTimeout(() => {
    document.body.classList.remove('onTransition');
  }, transitionBetweenStepsTime + 30);

  useAppStore.getState().stepGoTo(stepName);

  if (useAppStore.getState().list) {
    setCurrentStepIndex();
    setPreviousAndNextStepsStatus();
    checkActionBarShowing();
  }

  document.querySelector('#root')?.scrollTo(0, 0);
}

function stepsUpdated(step: string, preventContinueEvent = false): void {
  const answer: AnswerMap | null | undefined = useQuestionsStore.getState().getAnswers({ questionName: step });
  const currentStepIndex = useAppStore.getState().currentStepIndex as number;
  const totalSteps = useAppStore.getState().totalSteps as number;
  const isItLastStep = currentStepIndex === (totalSteps - 1);
  const { savedAnswers } = useQuestionsStore.getState();
  const dataToSend: Record<string, { answers: unknown }> = {};
  const { currentStep } = useAppStore.getState();
  const { previousStep } = useAppStore.getState();
  const analyticsQuestionName = getAnalyticsQuestionName(step);

  if (isItLastStep) {
    send({
      event: {
        event: 'funnel on onboarding',
        quiz_action: 'quiz end',
      },
    });

    Object.entries(savedAnswers as Record<string, Record<string, unknown>>).forEach((el) => {
      if (Object.keys(el[1]).length !== 0) {
        dataToSend[`${el[0]}`] = { answers: el[1] };
      }
    });

    if (dataSended) return;
    dataSended = true;
  }

  if (
    currentStepIndex === 1
    && (configs.stepsDetails as StepsDetailsMap)[previousStep || '']?.welcome_loader
    && !quizStarted
  ) {
    quizStarted = true;
    send({
      event: {
        event: 'quiz_funnel',
        quiz_action: 'start_quiz',
      },
    });
  }

  if (!quizStarted && !(configs.stepsDetails as StepsDetailsMap)[currentStep || '']?.welcome_loader) {
    quizStarted = true;
    send({
      event: {
        event: 'quiz_funnel',
        quiz_action: 'start_quiz',
      },
    });
  }

  if (answer && answer.value && answer.units) {
    send({
      event: {
        event: 'onboarding_question',
        quiz_question: analyticsQuestionName,
        quiz_answer: answer.value.value,
        quiz_answer_units: answer.units.value,
      },
    });

    return;
  }

  let answers: string | string[] | undefined;

  if (step === 'mail') {
    const mail = useQuestionsStore.getState().getAnswers({ questionName: 'mail' }) as AnswerMap;
    const agreeToInbox = (useQuestionsStore.getState().getAnswers({ questionName: 'mail' }) as AnswerMap).agreeToInbox || true;

    send({
      event: {
        event: 'onboarding_question',
        quiz_question: 'User has agreed to inbox',
        quiz_answer: agreeToInbox,
      },
    });

    send({
      event: {
        email: mail.value,
      },
    });

    if (window.web_platform === 'facebook_web' && window.fbq) {
      window.fbq('track', 'contact');
    }

    send({
      event: {
        event: 'funnel on onboarding',
        quiz_action: 'email send',
        payment_type: mail.value,
      },
    });

    return;
  }

  if (step === 'customV2_name') {
    const name = useQuestionsStore.getState().getAnswers({ questionName: 'customV2_name' }) as AnswerMap;
    const nameVal = Object.keys(name)[0];

    if (nameVal) {
      send({
        event: {
          event: 'onboarding_question',
          quiz_question: 'name',
          quiz_answer: nameVal,
        },
      });
    } else {
      send({
        event: {
          event: 'onboarding_question',
          quiz_question: 'name',
          quiz_answer: 'not_say',
        },
      });
    }

    return;
  }

  if (step === 'customV2_date') {
    const date = useQuestionsStore.getState().getAnswers({ questionName: 'customV2_date' }) as AnswerMap;
    const dateVal = Object.keys(date)[0];

    if (dateVal) {
      send({
        event: {
          event: 'onboarding_question',
          quiz_question: 'date',
          quiz_answer: dateVal,
        },
      });
    } else {
      send({
        event: {
          event: 'onboarding_question',
          quiz_question: 'date',
          quiz_answer: 'none',
        },
      });
    }

    return;
  }

  if (step === 'customV2_breed') {
    const breed = useQuestionsStore.getState().getAnswers({ questionName: 'customV2_breed' }) as AnswerMap;
    const breedVal = Object.keys(breed)[0];

    if (breedVal) {
      send({
        event: {
          event: 'onboarding_question',
          quiz_question: 'breed',
          quiz_answer: breedVal?.toLowerCase()?.replace(/ /g, '_'),
        },
      });
    } else {
      send({
        event: {
          event: 'onboarding_question',
          quiz_question: 'breed',
          quiz_answer: 'dont_know',
        },
      });
    }

    return;
  }

  if (step === 'receive_email') {
    // Событие receive_email шлёт сам компонент receiveEmail (с реальным ответом
    // sure/not_interested). Здесь выходим, чтобы не отправить дубль с quiz_answer: 'none'.
    return;
  }

  if ((configs.stepsDetails as StepsDetailsMap)[currentStep || '']?.continue) {
    if (!preventContinueEvent) {
      send({
        event: {
          event: 'onboarding_question',
          quiz_question: analyticsQuestionName,
          quiz_answer: 'continue',
        },
      });
    }
    return;
  }

  if ((configs.stepsDetails as StepsDetailsMap)[currentStep || '']?.welcome_loader) {
    send({
      event: {
        event: 'onboarding_question',
        quiz_question: 'welcome_loader',
        quiz_answer: 'none',
      },
    });
    return;
  }

  if (answer) {
    const answerKeys = Object.keys(answer);
    if (answerKeys.length > 1) {
      answers = [];
      answerKeys.forEach((key) => {
        if (answer[key]?.a_value) {
          (answers as string[]).push(answer[key].a_value);
        }
      });
    } else if (answerKeys.length === 1) {
      answers = answer[answerKeys[0]]?.a_value;
    }
  }

  send({
    event: {
      event: 'onboarding_question',
      quiz_question: analyticsQuestionName,
      quiz_answer: answers || 'none',
    },
  });

  if ((configs.stepsDetails as StepsDetailsMap)[currentStep || '']?.continueAuto) {
    send({
      event: {
        event: 'onboarding_question',
        quiz_question: analyticsQuestionName,
        quiz_answer: 'continueAuto',
      },
    });
  }
}

function navigateForward({ preventAction, numberOfSteps, preventContinueEvent }: ForwardArgs): void {
  const numberOfStepsValue = numberOfSteps || 1;
  if (preventActionFromFiring) return;
  clearTimeout(preventActionTimer);

  if (preventAction) {
    preventActionFromFiring = true;

    preventActionTimer = setTimeout(() => {
      preventActionFromFiring = false;
    }, preventAction);
  }

  const appState = useAppStore.getState();
  const stepsList = appState.list as StepItem[];
  const { currentStep } = appState as unknown as { currentStep: string };
  let stepAfterCurrent: string | null = null;

  const answer: AnswerMap = useQuestionsStore.getState().getAnswers({
    questionName: currentStep === 'customV2_email' ? 'mail' : currentStep,
  }) || {};
  const [keys] = Object.keys(answer);

  if (currentStep === 'customV2_reason' && (answer[keys]?.index === 'v8' || !answer[keys])) {
    stepsList.forEach((step, index) => {
      if (step.name === currentStep && stepsList[index + 3]) {
        stepAfterCurrent = stepsList[index + 3].name;
      }
    });
  } else {
    stepsList.forEach((step, index) => {
      if (step.name === currentStep && stepsList[index + numberOfStepsValue]) {
        stepAfterCurrent = stepsList[index + numberOfStepsValue].name;
      }
    });
  }

  markStepPassed(currentStep);
  stepsUpdated(currentStep, preventContinueEvent);

  if (!stepAfterCurrent) {
    // getLinkToPaymentPage тянет crypto-js (~40kb) и нужен только в конце воронки.
    // Грузим отдельным чанком; webpackPrefetch подтягивает его в idle заранее,
    // поэтому на момент редиректа задержки нет.
    import(/* webpackPrefetch: true */ '../utils/scripts/getLinkToPaymentPage')
      .then(({ default: getLinkToPaymentPage }) => {
        const link = getLinkToPaymentPage();

        setTimeout(() => {
          localStorage.removeItem('bags');
          window.location.href = link;
        }, 200);
      })
      .catch((e) => {
        console.warn('Failed to build payment link:', e);
        useAppStore.getState().toggleLoader(false);
      });

    return;
  }

  navigateTo(stepAfterCurrent);
}

function navigateBack({ numberOfSteps }: BackArgs = {}): void {
  const numberOfStepsValue = numberOfSteps || 1;
  const stepsList = useAppStore.getState().list as StepItem[];
  const { currentStep } = useAppStore.getState() as { currentStep: string };
  let stepBeforeCurrent: string | null = null;

  send({
    event: {
      event: 'quiz_funnel',
      quiz_action: 'onboarding_back',
      screen_name: currentStep,
    },
  });

  stepsList.forEach((question, index) => {
    if (question.name === currentStep && stepsList[index - numberOfStepsValue]) {
      stepBeforeCurrent = stepsList[index - numberOfStepsValue].name;
    }
  });

  if (!stepBeforeCurrent) {
    console.warn('Can\'t navigate back because you at the start of stepsList');
    return;
  }

  navigateTo(stepBeforeCurrent);
}

const steps = {
  setup: ({ stepsConfig, currentStep, stepsDetails }: SetupArgs): void => {
    transitionBetweenStepsTime = getTransitionBetweenStepsTime();
    const stepsList: StepItem[] = [];
    const separationStepIndexes: number[] = [];
    const hideHeaderStepIndexes: number[] = [];
    const hideProgressStepIndexes: number[] = [];
    const progressTitlesBySection: string[] = [];
    let sectionIndex = 0;

    stepsConfig.forEach((step, index) => {
      stepsList.push(createStep(step.name, stepsDetails[step.name]?.showActionBar));

      if (step.separationStep) {
        separationStepIndexes.push(index);

        sectionIndex += 1;

        if (step.progressTitle) {
          progressTitlesBySection[sectionIndex] = step.progressTitle;
        }
      }

      if (step.hideHeader) {
        hideHeaderStepIndexes.push(index);
      }

      if (step.hideProgress) {
        hideProgressStepIndexes.push(index);
      }

      if (index === 0 && step.progressTitle) {
        progressTitlesBySection[0] = step.progressTitle;
      }
    });

    useAppStore.getState().setSteps(stepsList);

    useAppStore.getState().setTotalSteps(stepsList.length);

    useAppStore.getState().setStepsMeta({
      separationStepIndexes,
      hideHeaderStepIndexes,
      hideProgressStepIndexes,
      progressTitlesBySection,
    });

    if (currentStep) navigateTo(currentStep);
    setCurrentStepIndex();
  },
  navigate: {
    to: navigateTo,
    forward: navigateForward,
    back: navigateBack,
  },
  userMovedToNextStepByClick: ({ byNextStep }: UserMovedArgs): void => {
    const { nextStep } = useAppStore.getState();
    const { currentStep } = useAppStore.getState();
    const step = byNextStep ? nextStep : currentStep;

    if (step === 'mail' && document.querySelector('#mail')) {
      (document.querySelector('#mail') as HTMLElement).focus();

      setTimeout(() => window.scrollTo(0, -285), 100);
    }
  },
  userMovedToPreviousStepByClick: (): void => {
    const { currentStep } = useAppStore.getState();

    if (currentStep === 'mail' && document.querySelector('#mail')) {
      (document.querySelector('#mail') as HTMLElement).focus();
      setTimeout(() => window.scrollTo(0, -285), 100);
    }
  },
  analytics: {
    send,
  },
};

export default steps;
